import { motion } from "framer-motion";
import { PhoneX, EnvelopeSimple, Voicemail, Database, FileText, ArrowDown } from "@phosphor-icons/react";
import { Chapter, Reveal, MaskedLine } from "@/components/Reveal";

const MISSED = [
    { icon: PhoneX, time: "09:41", title: "Missed call", sub: "No caller captured. No callback.", rot: "-rotate-2" },
    { icon: EnvelopeSimple, time: "11:07", title: "Website enquiry", sub: "Unanswered for 3 days.", rot: "rotate-1" },
    { icon: Voicemail, time: "13:52", title: "Voicemail × 2", sub: "Never listened to.", rot: "-rotate-1" },
    { icon: Database, time: "CRM", title: "Last updated", sub: "Six weeks ago.", rot: "rotate-2" },
    { icon: FileText, time: "TUE", title: "Quote sent", sub: "No follow-up. Ever.", rot: "-rotate-2" },
];

export default function Problem() {
    return (
        <section id="problem" className="relative overflow-hidden py-28 md:py-36" data-testid="problem-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <Chapter number="01" label="The problem" />
                <div className="mt-10 grid grid-cols-1 gap-16 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <h2 className="font-display text-4xl font-black leading-[0.98] tracking-tighter text-white sm:text-5xl" data-testid="problem-headline">
                            <MaskedLine delay={0.1}><span className="block">This is Tuesday</span></MaskedLine>
                            <MaskedLine delay={0.25}><span className="block text-white/50">at most businesses.</span></MaskedLine>
                        </h2>
                        <Reveal delay={0.35}>
                            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-white/60 md:text-lg">
                                A visitor lands on your website. Nothing responds. An enquiry sits unread.
                                The phone rings while you're on a job. The CRM hasn't been touched in weeks.
                                Not because you don't care — because no human can be everywhere at once.
                            </p>
                        </Reveal>
                    </div>
                    <div className="relative lg:col-span-7">
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                            {MISSED.map((m, i) => (
                                <motion.div
                                    key={m.title}
                                    initial={{ opacity: 0, y: 30, rotate: 0 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ delay: i * 0.22, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                    className={`border border-white/12 bg-surface p-5 ${m.rot} ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}
                                    data-testid={`missed-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <m.icon size={20} className="text-white/60" />
                                        <span className="font-mono text-[10px] tracking-widest text-white/30">{m.time}</span>
                                    </div>
                                    <p className="mt-4 text-sm font-bold text-white/85">{m.title}</p>
                                    <p className="mt-1 text-xs font-light text-white/40">{m.sub}</p>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.94 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 1.3, duration: 0.9 }}
                                className="col-span-2 flex items-center justify-center border border-white/20 bg-white/[0.04] p-5 backdrop-blur-sm sm:col-span-1"
                            >
                                <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                    Freeze.
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </div>

                <div className="mt-28 max-w-4xl">
                    <h3 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl" data-testid="problem-statement">
                        <MaskedLine delay={0.1}><span className="block">Every missed enquiry is an opportunity</span></MaskedLine>
                        <MaskedLine delay={0.3}><span className="block text-white/50">that may never come back.</span></MaskedLine>
                    </h3>
                    <Reveal delay={0.5}>
                        <button
                            onClick={() => document.getElementById("system")?.scrollIntoView({ behavior: "smooth" })}
                            data-testid="problem-connect-button"
                            className="group mt-10 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:text-white/70"
                        >
                            What if you could connect the entire journey?
                            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    </Reveal>
                </div>
            </div>

            <Reveal className="mt-24" y={40}>
                <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
                    <div className="relative aspect-[21/9] overflow-hidden">
                        <img
                            src="/assets/img/transformation.jpg"
                            alt="A traditional business transformed into an automated digital company"
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/35" />
                        <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                            Manual → Engineered
                        </p>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}
